// client/src/pages/DesignAssistant.jsx
import { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { CREDIT_FEATURES, quoteCredits, commitCredits } from '../utils/creditApi';
import { useCredits } from '../contexts/CreditContext';
import { toast } from 'react-toastify';
import shedBg from '../assets/guest-house.jpg';

const GREETING_MESSAGE = {
  role: 'assistant',
  content:
    "Tell me about your project. What are you building, approximate dimensions, and any preferred materials? I will draft a materials list once we have enough detail.",
};

const isGreetingMessage = (message) => {
  if (!message || message.role !== 'assistant') return false;
  const content = typeof message.content === 'string' ? message.content.trim() : '';
  if (!content) return false;

  const normalized = content.toLowerCase();
  if (normalized === GREETING_MESSAGE.content.trim().toLowerCase()) return true;
  return normalized.includes('tell me about your project') && normalized.includes('materials list');
};

const MAX_HISTORY = 50;

// Cap for how many realtime rows to render
const PRICE_ROWS_TO_SHOW = 50; // adjust if desired
const currencyFormatter =
  typeof Intl !== 'undefined'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    : null;

const normalizeQueryKey = (value) => String(value || '').trim().toLowerCase();

const parsePriceValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (!value) return null;
  const cleaned = String(value).replace(/[^0-9.,-]/g, '');
  if (!cleaned) return null;
  const normalized = cleaned.replace(/,/g, '');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value) => {
  if (!Number.isFinite(value)) return '-';
  if (currencyFormatter) return currencyFormatter.format(value);
  return `$${value.toFixed(2)}`;
};

const sanitizeExternalUrl = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return '';
  try {
    const normalized = new URL(trimmed);
    const protocol = normalized.protocol.toLowerCase();
    return protocol === 'http:' || protocol === 'https:' ? normalized.href : '';
  } catch {
    return '';
  }
};

const sanitizeFileName = (value, fallback = 'file') => {
  const raw = (typeof value === 'string' || typeof value === 'number') ? String(value) : '';
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || fallback;
};

const POLLUTION_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const sanitizeMaterialField = (value) => {
  const str = value === undefined || value === null ? '' : String(value);
  const lowered = str.trim().toLowerCase();
  return POLLUTION_KEYS.has(lowered) ? '' : str;
};

const sanitizeMaterialRecord = (material = {}) => {
  const item =
    material?.item ??
    material?.name ??
    material?.material ??
    material?.product ??
    material?.description ??
    material?.title ??
    material?.model ??
    '';
  const quantity =
    material?.quantity ??
    material?.qty ??
    material?.count ??
    material?.amount ??
    material?.pieces ??
    '';
  const unit = material?.unit ?? material?.units ?? material?.uom ?? material?.measure ?? '';
  const notes =
    material?.notes ??
    material?.note ??
    material?.detail ??
    material?.details ??
    material?.comments ??
    material?.comment ??
    '';

  return {
    item: sanitizeMaterialField(item),
    quantity: sanitizeMaterialField(quantity),
    unit: sanitizeMaterialField(unit),
    notes: sanitizeMaterialField(notes),
  };
};

const sanitizeStateKey = (value, fallback = '') => {
  const normalized = normalizeQueryKey(value);
  if (!normalized || POLLUTION_KEYS.has(normalized)) return fallback;
  return normalized;
};

const parseQuantityValue = (raw) => {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw).replace(/[^0-9.,-]/g, '');
  if (!cleaned) return null;
  const normalized = cleaned.replace(/,/g, '');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildQuantityDisplay = (material = {}) => {
  const value = parseQuantityValue(material?.quantity);
  if (!Number.isFinite(value)) return null;
  return Number.isInteger(value) ? value : Number(value.toFixed(2));
};

const normalizeCandidateRecord = (candidate = {}, fallbackQuery = '') => {
  if (!candidate || typeof candidate !== 'object') candidate = {};
  const query = String(candidate.query || candidate.item || fallbackQuery || '').trim();
  const title = String(candidate.title || candidate.item || candidate.name || candidate.description || candidate.model || '').trim();
  const price = candidate.price != null ? String(candidate.price).trim() : '';
  const source = String(candidate.source || candidate.vendor || candidate.supplier || candidate.brand || '').trim();
  const rawProductLink = String(candidate.product_link || candidate.url || candidate.link || candidate.href || '').trim();
  const rawUrl = String(candidate.url || rawProductLink || '').trim();
  const url = sanitizeExternalUrl(rawUrl);
  const productLink = sanitizeExternalUrl(rawProductLink) || url;
  return {
    query,
    title,
    price,
    source,
    url,
    product_link: productLink || url || '',
    vendor: String(candidate.vendor || candidate.source || '').trim(),
    raw: candidate,
  };
};

const normalizeRealtimeResults = (rawResults) => {
  if (!Array.isArray(rawResults)) return [];
  const byQuery = new Map();

  rawResults.forEach((entry, index) => {
    const baseQuery = typeof entry?.query === 'string' ? entry.query.trim() : '';
    const queryKey = normalizeQueryKey(baseQuery) || `entry-${index}`;

    let rawCandidates = Array.isArray(entry?.candidates) ? entry.candidates.slice() : [];
    const selectedRaw = entry?.selected;
    if (!rawCandidates.length && selectedRaw) {
      rawCandidates = [selectedRaw];
    }
    if (!rawCandidates.length && (!entry || !Array.isArray(entry?.candidates))) {
      rawCandidates = [entry];
    }

    let normalizedCandidates = rawCandidates
      .map((candidate) => normalizeCandidateRecord(candidate, baseQuery))
      .filter((candidate) => candidate && (candidate.title || candidate.price || candidate.url || candidate.query));

    const selectedFromEntry = selectedRaw ? normalizeCandidateRecord(selectedRaw, baseQuery) : null;

    if (selectedFromEntry && selectedFromEntry.title) {
      const selectedKey = (selectedFromEntry.url || selectedFromEntry.product_link || `${selectedFromEntry.title}|${selectedFromEntry.source}|${selectedFromEntry.price}`).toLowerCase();
      const existingIndex = normalizedCandidates.findIndex((candidate) => {
        const candidateKey = (candidate.url || candidate.product_link || `${candidate.title}|${candidate.source}|${candidate.price}`).toLowerCase();
        return candidateKey === selectedKey;
      });
      if (existingIndex === -1) {
        normalizedCandidates.unshift(selectedFromEntry);
      } else if (existingIndex > 0) {
        const [match] = normalizedCandidates.splice(existingIndex, 1);
        normalizedCandidates.unshift(match);
      }
    }

    const deduped = [];
    const seen = new Set();
    normalizedCandidates.forEach((candidate) => {
      const key = (candidate.url || candidate.product_link || `${candidate.title}|${candidate.source}|${candidate.price}`).toLowerCase();
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      deduped.push(candidate);
    });

    if (!deduped.length) {
      if (!byQuery.has(queryKey)) {
        byQuery.set(queryKey, {
          query: baseQuery,
          queryKey,
          candidates: [],
          selectedIndex: 0,
        });
      }
      return;
    }

    const trimmed = deduped.slice(0, 3);
    let selectedIndex = typeof entry?.selected_index === 'number' ? entry.selected_index : 0;
    if (selectedIndex < 0 || selectedIndex >= trimmed.length) {
      const pricedIndex = trimmed.findIndex((candidate) => candidate.price);
      selectedIndex = pricedIndex >= 0 ? pricedIndex : 0;
    }

    const existing = byQuery.get(queryKey);
    if (existing) {
      const merged = existing.candidates.slice();
      const seenMerged = new Set(merged.map((candidate) => (candidate.url || candidate.product_link || `${candidate.title}|${candidate.source}|${candidate.price}`).toLowerCase()));
      trimmed.forEach((candidate) => {
        const key = (candidate.url || candidate.product_link || `${candidate.title}|${candidate.source}|${candidate.price}`).toLowerCase();
        if (!seenMerged.has(key) && merged.length < 3) {
          merged.push(candidate);
          seenMerged.add(key);
        }
      });
      const selectedCandidate = trimmed[selectedIndex] || merged[existing.selectedIndex] || merged[0];
      const selectedKey = selectedCandidate
        ? (selectedCandidate.url || selectedCandidate.product_link || `${selectedCandidate.title}|${selectedCandidate.source}|${selectedCandidate.price}`).toLowerCase()
        : null;
      let mergedSelectedIndex = selectedKey
        ? merged.findIndex((candidate) => (candidate.url || candidate.product_link || `${candidate.title}|${candidate.source}|${candidate.price}`).toLowerCase() === selectedKey)
        : existing.selectedIndex;
      if (mergedSelectedIndex === -1) mergedSelectedIndex = existing.selectedIndex < merged.length ? existing.selectedIndex : 0;
      byQuery.set(queryKey, {
        query: existing.query || baseQuery,
        queryKey,
        candidates: merged,
        selectedIndex: mergedSelectedIndex,
      });
    } else {
      byQuery.set(queryKey, {
        query: baseQuery || trimmed[selectedIndex]?.query || '',
        queryKey,
        candidates: trimmed,
        selectedIndex,
      });
    }
  });

  return Array.from(byQuery.values());
};

const isFullUrl = (value) => typeof value === 'string' && /^(https?:|data:)/i.test(value);

const sanitizeImageSummary = (raw) => {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const fenceIndex = trimmed.indexOf('```');
  const withoutFence = fenceIndex >= 0 ? trimmed.slice(0, fenceIndex) : trimmed;
  const jsonStart = withoutFence.search(/\n\s*[{[]/);
  let display = jsonStart >= 0 ? withoutFence.slice(0, jsonStart) : withoutFence;
  if (/^\s*[{[]/.test(display)) {
    display = '';
  }
  return display.trim();
};

const summarizeForDisplay = (raw) => {
  const cleaned = sanitizeImageSummary(raw);
  if (!cleaned) return '';
  const sentences = cleaned.split(/(?<=[.!?])\s+/).filter(Boolean);
  let summary = sentences.slice(0, 2).join(' ') || cleaned;
  const maxLen = 260;
  if (summary.length > maxLen) summary = `${summary.slice(0, maxLen - 1).trim()}…`;
  return summary;
};

export default function DesignAssistant() {
  const [messages, setMessages] = useState(() => []);
  const [draft, setDraft] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);

  const [imageSummary, setImageSummary] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const imageSummaryForContext = useMemo(() => {
    const cleaned = sanitizeImageSummary(imageSummary);
    if (cleaned) return cleaned;
    if (typeof imageSummary === 'string') return imageSummary.trim();
    return '';
  }, [imageSummary]);
  const imageSummaryDisplay = imageSummaryForContext;
  const fileInputRef = useRef(null);

  const [rendering, setRendering] = useState(false);
  const [renderUrl, setRenderUrl] = useState('');
  const [renderPath, setRenderPath] = useState('');
  const [renderHistory, setRenderHistory] = useState([]);
  const [sharePosting, setSharePosting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTitle, setShareTitle] = useState('');
  const [shareDescription, setShareDescription] = useState('');
  const [estimation, setEstimation] = useState(null);
  const [showEstimateDetails, setShowEstimateDetails] = useState(false);
  const showSavedEstimationCard = false;

  const [generatingGuide, setGeneratingGuide] = useState(false);
  const [guideUrl, setGuideUrl] = useState('');
  const [guideTitle, setGuideTitle] = useState('');
  const [showGuidePreview, setShowGuidePreview] = useState(false);

  const [priceResults, setPriceResults] = useState([]);
  const [realtimeError, setRealtimeError] = useState('');
  const [lastRealtimeMeta, setLastRealtimeMeta] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  // "auto" (cards on xs, table on sm+), or force "table"/"cards"
  const [viewMode, setViewMode] = useState('table');
  const [wideTable, setWideTable] = useState(true);

  const matTopRef = useRef(null);
  const renameInputRef = useRef(null);
  const [showHistory, setShowHistory] = useState(false);
  const [convs, setConvs] = useState([]);
  const [convId, setConvId] = useState('');
  const [convTitle, setConvTitle] = useState('New conversation');
  const [titleEdited, setTitleEdited] = useState(false);
  const [renamingId, setRenamingId] = useState('');
  const [renameDraft, setRenameDraft] = useState('');

  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [communityBuilds, setCommunityBuilds] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const historyReadyRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastSavedHashRef = useRef('');

  const { balance: creditBalance, loading: creditsLoading, loaded: creditsLoaded, refreshBalance, setBalance } = useCredits();
  const sliderAnimationStyle = { animation: 'realtimeSlider 1.8s linear infinite' };
  const sliderKeyframes = `@keyframes realtimeSlider {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(200%); }
  }

  @keyframes focusFlash {
    0% {
      border-color: rgba(255,255,255,0.28);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.18), 0 0 0 8px rgba(255,255,255,0.06);
    }
    100% {
      border-color: transparent;
      box-shadow: none;
    }
  }`;

  const [renderQuote, setRenderQuote] = useState({
    totalCost: 3,
    available: creditBalance?.available ?? 0,
    shortfall: 0,
    loading: false,
    error: '',
  });
  const renderCreditRequestRef = useRef('');
  const generateRenderRequestId = () => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `design-${Date.now()}`;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchRenderQuote = async () => {
      setRenderQuote((prev) => ({
        ...prev,
        loading: true,
        error: '',
      }));
      try {
        const data = await quoteCredits([{ feature: 'design_visual', quantity: 1 }]);
        if (cancelled) return;
        const totalCost = Number(data?.totalCost ?? 3);
        const available = Number(data?.available ?? creditBalance?.available ?? 0);
        const shortfall = Math.max(0, Number(data?.shortfall ?? totalCost - available));
        setRenderQuote({
          totalCost,
          available,
          shortfall,
          loading: false,
          error: '',
        });
        if (Number.isFinite(available)) {
          setBalance((prev) => ({ ...prev, available }));
        }
      } catch (err) {
        if (cancelled) return;
        const available = Number(creditBalance?.available ?? 0);
        const message = err?.response?.data?.error || err?.message || 'Unable to quote credits.';
        const shortfall = available >= 3 ? 0 : Math.max(0, 3 - available);
        setRenderQuote({
          totalCost: 3,
          available,
          shortfall,
          loading: false,
          error: message,
        });
      }
    };

    fetchRenderQuote();

    return () => {
      cancelled = true;
    };
  }, [creditBalance?.available, setBalance]);

  useEffect(() => {
    let cancelled = false;
    const loadCommunityBuilds = async () => {
      try {
        const { data } = await api.get('/community-builds', { params: { limit: 10 } });
        if (cancelled) return;
        setCommunityBuilds(data?.builds || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load community builds:', err);
      }
    };
    loadCommunityBuilds();
    return () => {
      cancelled = true;
    };
  }, []);

  const card =
    'rounded-[24px] border border-white/10 bg-[radial-gradient(circle_at_10%_10%,rgba(246,196,82,0.08),transparent_22%),radial-gradient(circle_at_90%_0%,rgba(94,234,212,0.05),transparent_26%),rgba(6,6,8,0.86)] ' +
    'shadow-[0_30px_80px_rgba(0,0,0,0.7)] backdrop-blur-2xl';
  const action =
    'inline-flex items-center justify-center rounded-full border border-white/12 bg-white/5 px-4 py-2 text-sm sm:text-xs text-zinc-100 ' +
    'hover:border-amber-300/60 hover:text-white transition active:translate-y-px disabled:opacity-60';
  const primary =
    'inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm sm:text-xs font-semibold ' +
    'text-slate-950 bg-gradient-to-r from-amber-400 to-amber-600 ' +
    'shadow-[0_12px_35px_rgba(246,196,82,0.45)] hover:brightness-110 active:translate-y-px disabled:opacity-50';
  const subPanel =
    'rounded-2xl border border-white/10 bg-[rgba(10,10,12,0.75)] shadow-[0_18px_60px_rgba(0,0,0,0.55)]';
  const renderPreviewBlock = (() => {
    if (!renderUrl) return null;
    const safeRenderUrl = sanitizeExternalUrl(renderUrl);
    if (!safeRenderUrl) return null;
    return (
      <div className={`${subPanel} overflow-hidden`}>
        <img src={safeRenderUrl} alt="AI render preview" className="block w-full object-cover" />
        <div className="flex justify-end border-t border-white/10 bg-white/5 p-2">
          <a href={safeRenderUrl} download className={action}>
            Download
          </a>
        </div>
      </div>
    );
  })();
  const hasMaterials = Array.isArray(materials) && materials.length > 0;
  const bubbleUser =
    'p-4 sm:p-5 rounded-2xl border border-white/10 bg-[linear-gradient(160deg,rgba(255,255,255,0.05),rgba(10,10,12,0.9))] ' +
    'shadow-[0_16px_50px_rgba(0,0,0,0.55)] text-slate-100';
  const bubbleAssistant =
    'p-4 sm:p-5 rounded-2xl border border-amber-200/30 bg-[linear-gradient(160deg,rgba(60,50,30,0.4),rgba(14,12,10,0.9))] ' +
    'shadow-[0_16px_50px_rgba(0,0,0,0.55)] text-amber-50';
  const scenicBg =
    `url(${shedBg})`;
  const pageBgStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(5,5,7,0.94), rgba(5,5,7,0.92)), ${scenicBg}`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
  };

  /* ------------------------- helpers ------------------------- */
  function setMaterialsSanitized(list) {
    const next = Array.isArray(list) ? list.map((m) => sanitizeMaterialRecord(m)) : [];
    setMaterials(next);
  }

  function updateMaterialField(index, field, value) {
    if (index == null || index < 0) return;
    setMaterials((prev) => {
      if (!Array.isArray(prev) || index >= prev.length) return prev;
      const next = [...prev];
      const current = prev[index] || {};
      const updated = {
        item: current.item,
        quantity: current.quantity,
        unit: current.unit,
        notes: current.notes,
      };
      updated[field] = value;
      next[index] = sanitizeMaterialRecord(updated);
      return next;
    });
  }

  function extractMaterialsFrom(text) {
    if (!text) return null;
    const candidates = [];
    const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fence?.[1]) candidates.push(fence[1]);
    const objMatch = text.match(/\{[\s\S]*?"materials"[\s\S]*?\}/);
    if (objMatch) candidates.push(objMatch[0]);
    const arrMatch = text.match(/\[[\s\S]*?\]/);
    if (arrMatch) candidates.push(arrMatch[0]);
    for (const c of candidates) {
      try {
        const parsed = JSON.parse(c);
        if (Array.isArray(parsed)) return parsed;
        if (parsed?.materials && Array.isArray(parsed.materials)) return parsed.materials;
      } catch {}
    }
    return null;
  }

  function sanitizeAssistantText(text) {
    if (!text) return '';
    let output = String(text);

    output = output.replace(/(^|\n)\s*-\s+/g, '$1• ');
    output = output.replace(/[–—]+/g, '. ');
    output = output.replace(/(\d)\s*-\s*(\d)/g, '$1 to $2');
    output = output.replace(/-/g, ' ');
    output = output.replace(/\bquick questions?\b/gi, 'questions');
    output = output.replace(/\bquick\b/gi, '');
    output = output.replace(/\s+([.,!?;:])/g, '$1');
    output = output.replace(/\s{2,}/g, ' ').trim();
    return output;
  }

  function formatAssistantReply(reply, materialsDraft) {
    const raw = typeof reply === 'string' ? reply : '';
    const trimmed = raw.trim();

    if (!trimmed) return 'OK.';
    if (!Array.isArray(materialsDraft) || materialsDraft.length === 0) {
      return sanitizeAssistantText(raw) || 'OK.';
    }

    const withoutFences = trimmed.replace(/```(?:json)?[\s\S]*?```/gi, '').trim();

    const strippedStructured = withoutFences
      .replace(/\[[\s\S]*?\]/g, (segment) => {
        try {
          const parsed = JSON.parse(segment);
          return Array.isArray(parsed) ? '' : segment;
        } catch {
          return segment;
        }
      })
      .replace(/\{[\s\S]*?\}/g, (segment) => {
        try {
          const parsed = JSON.parse(segment);
          if (Array.isArray(parsed?.materials)) return '';
          if (parsed?.materials && typeof parsed.materials === 'object') return '';
          return segment;
        } catch {
          return segment;
        }
      })
      .trim();

    if (strippedStructured) return sanitizeAssistantText(strippedStructured) || 'OK.';
    if (withoutFences) return sanitizeAssistantText(withoutFences) || 'OK.';
    if (/^[\[{]/.test(trimmed)) {
      return 'I drafted a materials list and loaded it into the Materials panel for you.';
    }
    return sanitizeAssistantText(trimmed) || 'OK.';
  }

  function slugify(value = '') {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      || 'guide';
  }

  // Map many possible estimation payloads → the rows we show in the “Realtime Price Suggestions” table
  function rowsFromEstimation(est) {
    const data = est?.data || {};
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.pricing)) return data.pricing;
    if (Array.isArray(data.lines)) return data.lines;
    return [];
  }

  function applyPriceResults(rawResults, meta = undefined) {
    const normalized = normalizeRealtimeResults(rawResults);
    setPriceResults(normalized);
    setExpandedRows({});
    if (meta !== undefined) {
      setLastRealtimeMeta(meta);
    }
  }

  function clearRealtimeState() {
    setPriceResults([]);
    setLastRealtimeMeta(null);
    setExpandedRows({});
    try {
      localStorage.removeItem('design_realtime_prices');
    } catch (err) {
      console.warn('[design realtime-prices] cache clear failed', err);
    }
  }

  const buildCommunityMaterials = () => {
    const baseMaterials = Array.isArray(materials) ? materials : [];
    const priceRows = Array.isArray(priceResults) ? priceResults : [];

    const merged = baseMaterials.map((mat, idx) => {
      const entry = priceRows[idx];
      const candidate =
        entry && Array.isArray(entry.candidates)
          ? entry.candidates[entry.selectedIndex ?? 0] || entry.candidates[0]
          : null;
      const parsedCost = candidate ? parsePriceValue(candidate.price) : parsePriceValue(mat.cost);
      return {
        item: mat.item || mat.name || '',
        quantity: mat.quantity || '',
        unit: mat.unit || '',
        notes: mat.notes || '',
        source: mat.source || candidate?.source || candidate?.vendor || '',
        product_link: candidate?.product_link || candidate?.url || '',
        cost: Number.isFinite(parsedCost) ? parsedCost : null,
      };
    });

    if (merged.length) {
      return merged.filter((m) => m.item || m.notes || m.cost != null);
    }

    const fallback = priceRows.map((entry) => {
      const candidate =
        entry && Array.isArray(entry.candidates)
          ? entry.candidates[entry.selectedIndex ?? 0] || entry.candidates[0]
          : entry?.selected || null;
      const parsedCost = candidate ? parsePriceValue(candidate.price) : null;
      return {
        item: entry?.query || candidate?.title || '',
        quantity: '',
        unit: '',
        notes: candidate?.title || '',
        source: candidate?.source || candidate?.vendor || '',
        product_link: candidate?.product_link || candidate?.url || '',
        cost: Number.isFinite(parsedCost) ? parsedCost : null,
      };
    });
    return fallback.filter((m) => m.item || m.cost != null);
  };

  const sanitizePlain = (value) => (typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '');

  const shareToCommunity = async () => {
    if (sharePosting) return;
    const items = buildCommunityMaterials();
    if (!items.length && !renderUrl) {
      toast.error('Add materials or a render before sharing.');
      return;
    }
    const safeTitle = sanitizePlain(shareTitle);
    if (!safeTitle) {
      toast.error('Please add a title for your community build.');
      return;
    }
    const safeDescription = sanitizePlain(shareDescription).slice(0, 2000);
    setSharePosting(true);
    try {
      await api.post('/community-builds', {
        title: safeTitle,
        description: safeDescription,
        renderUrl,
        materials: items,
      });
      toast.success('Shared to Community Builds!');
      setShareOpen(false);
    } catch (err) {
      console.error('[shareToCommunity]', err);
      toast.error(err?.response?.data?.error || 'Failed to share build.');
    } finally {
      setSharePosting(false);
    }
  };

  const toggleRowExpanded = (key) => {
    const safeKey = sanitizeStateKey(key);
    if (!safeKey) return;
    setExpandedRows((prev) => ({
      ...prev,
      [safeKey]: !prev?.[safeKey],
    }));
  };

  const handleCandidateSelect = (entryKey, candidateIndex) => {
    if (!entryKey || candidateIndex == null) return;
    setPriceResults((prev) =>
      prev.map((entry) => {
        const key = entry?.queryKey || normalizeQueryKey(entry?.query);
        if (key !== entryKey) return entry;
        if (!Array.isArray(entry.candidates) || candidateIndex < 0 || candidateIndex >= entry.candidates.length) {
          return entry;
        }
        if (entry.selectedIndex === candidateIndex) return entry;
        return { ...entry, selectedIndex: candidateIndex };
      })
    );
  };

  // When we want to force the saved estimate into the visible table
  function loadSavedIntoPriceTable() {
    const rows = rowsFromEstimation(estimation);
    if (rows?.length) {
      applyPriceResults(rows);
      setRealtimeError('');
      try {
        localStorage.removeItem('design_realtime_prices');
      } catch (err) {
        console.warn('[design realtime-prices] cache clear failed', err);
      }
    }
  }

  function buildRenderPayload() {
    const userTurns = messages
      .filter((m) => m?.role === 'user' && m.content)
      .map((m) => String(m.content));
    const sections = [];
    if (imageSummaryDisplay) sections.push('Image summary: ' + imageSummaryDisplay);
    if (userTurns.length) sections.push('Initial brief: ' + userTurns[0]);
    if (userTurns.length > 1) sections.push('Latest update: ' + userTurns[userTurns.length - 1]);
    const goalRaw =
      sections.join('\n\n') || 'Render the finished build described in the latest conversation.';
    const goal = goalRaw.length > 600 ? goalRaw.slice(0, 597) + '...' : goalRaw;

    const combined = userTurns.join(' ');
    const dimsMatch = combined.match(/\b\d+(?:\.\d+)?\s?(?:x|by)\s?\d+(?:\.\d+)?\b/i);
    const typeMatch = combined.match(
      /\b(shed|deck|fence|pergola|gazebo|playhouse|garage|cabin|tiny house|patio|greenhouse|barn|stairs|platform|porch|office|studio|addition|pavilion)\b/i
    );
    const styleMatch = combined.match(
      /\b(modern|contemporary|rustic|traditional|industrial|farmhouse|coastal|scandinavian|minimalist|mediterranean|boho|craftsman)\b/i
    );
    const environmentMatch = combined.match(
      /\b(backyard|forest|woodland|mountain|coastal|desert|urban|suburban|lakeside|garden|courtyard|rooftop)\b/i
    );

    const primaryMaterials = materials
      .slice(0, 6)
      .map((m) => [m.quantity, m.unit, m.item].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(', ');

    const featuresText = userTurns.slice(-2).join(' ').replace(/\s+/g, ' ').trim().slice(0, 220);

    return {
      goal,
      brief: {
        type: typeMatch ? typeMatch[1] : '',
        dimensions: dimsMatch ? dimsMatch[0].replace(/by/i, 'x') : '',
        primaryMaterials: primaryMaterials || '',
        features: featuresText,
        colorScheme: '',
        style: styleMatch ? styleMatch[1] : '',
        environment: environmentMatch ? environmentMatch[1] : '',
        extraNotes: imageSummaryForContext || '',
        view: 'landscape',
        viewpoint: 'eye-level three-quarter view',
      },
    };
  }

  useEffect(() => {
    if (!lastRealtimeMeta) return;
    if (!Array.isArray(priceResults) || !priceResults.length) return;
    try {
      localStorage.setItem(
        'design_realtime_prices',
        JSON.stringify({
          ...lastRealtimeMeta,
          results: priceResults,
        })
      );
    } catch (err) {
      console.warn('[design realtime-prices] cache persist failed', err);
    }
  }, [priceResults, lastRealtimeMeta]);


  /* ------------------------- conversation history ------------------------- */
  function deriveTitle(msgs) {
    const firstUser = (msgs || []).find((m) => m.role === 'user');
    if (!firstUser || !firstUser.content) return 'New conversation';
    const t = String(firstUser.content).replace(/\s+/g, ' ').trim();
    return t.length > 60 ? `${t.slice(0, 57)}...` : t || 'New conversation';
  }

  function buildPayloadFromState(entry) {
    const trimmedMessages = Array.isArray(entry.messages) ? entry.messages.slice(-200) : [];
    const trimmedMaterials = Array.isArray(entry.materials) ? entry.materials.slice(0, 200) : [];
    const estimationCopy =
      entry.estimation && typeof entry.estimation === 'object'
        ? JSON.parse(JSON.stringify(entry.estimation))
        : null;
    const providedTitle = typeof entry.title === 'string' ? entry.title.trim() : '';
    const normalizedRenders = Array.isArray(entry.renders)
      ? entry.renders.slice(0, 50).map((r) => ({
          path: r?.path || '',
          bytes: Number.isFinite(r?.bytes) ? Number(r.bytes) : undefined,
          createdAt: r?.createdAt || new Date().toISOString(),
        })).filter((r) => r.path)
      : [];

    return {
      title: providedTitle || deriveTitle(trimmedMessages),
      messages: trimmedMessages.map((m) => ({ ...m })),
      materials: trimmedMaterials.map((m) => ({ ...m })),
      imageSummary: entry.imageSummary || '',
      renderUrl: isFullUrl(entry.renderUrl) ? entry.renderUrl : '',
      renderPath: typeof entry.renderPath === 'string' ? entry.renderPath : '',
      renders: normalizedRenders,
      viewMode: 'table',
      estimation: estimationCopy,
    };
  }

  function hydrateConversation(convo) {
    const safeMessages =
      Array.isArray(convo?.messages) && convo.messages.length ? convo.messages : [];
    const safeMaterials = Array.isArray(convo?.materials) ? convo.materials : [];
    const safeImageSummary = convo?.imageSummary || '';
    const safeRenderUrl = convo?.renderUrl || '';
    const safeRenderPath = typeof convo?.renderPath === 'string' ? convo.renderPath : '';
    const safeRenders = Array.isArray(convo?.renders)
      ? convo.renders.map((r) => ({ ...r }))
      : [];
    const normalizedRenderUrl = isFullUrl(safeRenderUrl) ? safeRenderUrl : '';
    const safeViewMode = 'table';
    const safeEstimation =
      convo?.estimation && typeof convo.estimation === 'object'
        ? JSON.parse(JSON.stringify(convo.estimation))
        : null;
    const visibleMessages = safeMessages
      .filter((m) => !isGreetingMessage(m))
      .map((m) => {
        if (!m || typeof m !== 'object') return m;
        const content = typeof m.content === 'string' ? m.content : '';
        if (m.role === 'assistant') {
          const sanitized = sanitizeAssistantText(content);
          return { ...m, content: sanitized || content };
        }
        return { ...m, content };
      })
      .filter(Boolean);

    const derivedTitle = deriveTitle(visibleMessages);
    const storedTitle = (convo?.title && convo.title.trim()) || derivedTitle;

    setConvTitle(storedTitle);
    setTitleEdited(convo ? storedTitle !== derivedTitle : false);
    setRenamingId('');
    setRenameDraft('');
    setMessages(visibleMessages);
    setMaterialsSanitized(safeMaterials);
    setImageSummary(safeImageSummary);
    setRenderUrl(normalizedRenderUrl);
    setRenderPath(safeRenderPath);
    setRenderHistory(safeRenders);
    setViewMode(safeViewMode);
    setEstimation(safeEstimation);

    // ⬇️ Also hydrate the price table from the saved estimate (if present)
    const savedRealtime = convo?.realtimePricing;
    const hasRealtimeRows = Array.isArray(savedRealtime?.results) && savedRealtime.results.length > 0;

    if (hasRealtimeRows) {
      applyPriceResults(savedRealtime.results, {
        normalizedItems: Array.isArray(savedRealtime?.normalizedItems) ? savedRealtime.normalizedItems : [],
        totalRequested: savedRealtime?.totalRequested ?? savedRealtime.results.length,
        fetchedAt: savedRealtime?.fetchedAt || new Date().toISOString(),
      });
    } else {
      const rows = rowsFromEstimation(safeEstimation);
      if (rows?.length) {
        applyPriceResults(rows);
      } else {
        clearRealtimeState();
      }
    }

    const payload = buildPayloadFromState({
      title: storedTitle,
      messages: visibleMessages,
      materials: safeMaterials,
      imageSummary: safeImageSummary,
      renderUrl: normalizedRenderUrl,
      renderPath: safeRenderPath,
      renders: safeRenders,
      viewMode: safeViewMode,
      estimation: safeEstimation,
    });
    lastSavedHashRef.current = JSON.stringify(payload);
    setSaveError('');
  }

  const normalizeConversationsFromServer = (rawList = []) =>
    rawList.map((item) => ({
      ...item,
      renderUrl: isFullUrl(item?.renderUrl) ? item.renderUrl : '',
    }));

  async function reloadConversationList() {
    try {
      const { data } = await api.get('/design/conversations');
      const raw = Array.isArray(data?.conversations) ? data.conversations : [];
      const normalized = normalizeConversationsFromServer(raw);
      setConvs(normalized);
    } catch (err) {
      console.warn('[reloadConversationList] failed', err?.response?.data || err);
    }
  }

  // Bootstrap: always start with a fresh conversation when opening the Design Assistant.
  useEffect(() => {
    let active = true;

    async function bootstrap() {
      setHistoryLoading(true);
      setHistoryError('');
      historyReadyRef.current = false;
      try {
        localStorage.removeItem('design_last_estimation');
        localStorage.removeItem('design_realtime_prices');
      } catch {}

      let normalizedList = [];
      try {
        const { data } = await api.get('/design/conversations');
        if (active) {
          const list = Array.isArray(data?.conversations) ? data.conversations : [];
          normalizedList = normalizeConversationsFromServer(list);
          setConvs(normalizedList);
        }
      } catch (err) {
        console.warn('[bootstrap] load conversations failed', err?.response?.data || err);
        if (active) {
          normalizedList = [];
          setConvs([]);
        }
      }

      const base = {
        title: 'New conversation',
        messages: [],
        materials: [],
        imageSummary: '',
        renderUrl: '',
        renderPath: '',
        renders: [],
        viewMode: 'table',
        estimation: null,
      };

      try {
        const { data: created } = await api.post('/design/conversations', base);
        if (!active) return;
        const convo = created?.conversation;
        if (!convo) throw new Error('Conversation was not created.');

        setConvs((prev) => {
          const rest = Array.isArray(prev) ? prev : normalizedList;
          const deduped = Array.isArray(rest) ? rest.filter((c) => c.id !== convo.id) : [];
          return [convo, ...deduped].slice(0, MAX_HISTORY);
        });
        setConvId(convo.id);
        setShowHistory(false);
        setDraft('');
        clearRealtimeState();
        hydrateConversation(convo);
      } catch (err) {
        if (!active) return;
        console.error('[bootstrap] create conversation failed', err);
        setHistoryError(err?.response?.data?.error || err.message || 'Failed to start a new conversation.');
        setConvId('');
        hydrateConversation(null);
      } finally {
        if (active) historyReadyRef.current = true;
        if (active) setHistoryLoading(false);
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, []);

  // If an estimation arrives later (e.g., after a save), auto-hydrate the price table once.
  useEffect(() => {
    if (estimation && priceResults.length === 0) {
      const rows = rowsFromEstimation(estimation);
      if (rows?.length) {
            applyPriceResults(rows);
          }
    }
    // eslint-disable-next-line
  }, [estimation]);

  useEffect(() => {
    if (titleEdited) return;
    const autoTitle = deriveTitle(messages);
    if (autoTitle && autoTitle !== convTitle) setConvTitle(autoTitle);
    // eslint-disable-next-line
  }, [messages, convTitle, titleEdited]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  function queueConversationSave(entry) {
    if (!entry?.id) return;

    // Check if conversation has meaningful content
    const hasMessages = Array.isArray(entry.messages) && entry.messages.length > 0;
    const hasMaterials = Array.isArray(entry.materials) && entry.materials.length > 0;
    const hasImageSummary = Boolean(entry.imageSummary?.trim());
    const hasRenderUrl = Boolean(entry.renderUrl?.trim());
    const hasEstimation = Boolean(entry.estimation);

    // Don't save empty conversations
    if (!hasMessages && !hasMaterials && !hasImageSummary && !hasRenderUrl && !hasEstimation) {
      return;
    }

    const payload = buildPayloadFromState(entry);
    const hash = JSON.stringify(payload);
    if (lastSavedHashRef.current === hash) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      saveTimerRef.current = null;
      setIsSaving(true);
      setSaveError('');
      try {
        await api.patch(`/design/conversations/${entry.id}`, payload);
        lastSavedHashRef.current = hash;
        setIsSaving(false);
      } catch (err) {
        console.error('[autoSaveConversation] error', err);
        setIsSaving(false);
        setSaveError(err?.response?.data?.error || 'Failed to save conversation.');
      }
    }, 600);
  }

  useEffect(() => {
    if (!historyReadyRef.current || !convId) return;

    const now = new Date().toISOString();
    const fallbackTitle = deriveTitle(messages);
    const title = convTitle?.trim() || fallbackTitle;

    const estimationCopy = estimation ? JSON.parse(JSON.stringify(estimation)) : null;
    const rendersCopy = Array.isArray(renderHistory) ? renderHistory.map((r) => ({ ...r })) : [];

    setConvs((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((c) => c.id === convId);
      const existing = idx >= 0 ? list[idx] : null;
      const entry = {
        id: convId,
        title,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        messages,
        materials,
        imageSummary,
        renderUrl,
        renderPath,
        renders: rendersCopy,
        viewMode,
        estimation: estimationCopy,
      };
      if (idx >= 0) list[idx] = { ...existing, ...entry };
      else list.unshift(entry);
      if (list.length > MAX_HISTORY) list.length = MAX_HISTORY;
      return list;
    });

    queueConversationSave({
      id: convId,
      title,
      messages,
      materials,
      imageSummary,
      renderUrl,
      renderPath,
      renders: rendersCopy,
      viewMode,
      estimation: estimationCopy,
    });
    // eslint-disable-next-line
  }, [convId, convTitle, messages, materials, imageSummary, renderUrl, renderPath, renderHistory, viewMode, estimation]);

  async function forceSaveConversation() {
    if (!historyReadyRef.current || !convId) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const estimationCopy = estimation ? JSON.parse(JSON.stringify(estimation)) : null;
    const fallbackTitle = deriveTitle(messages);
    const title = convTitle?.trim() || fallbackTitle;
    const entry = {
      id: convId,
      title,
      messages,
      materials,
      imageSummary,
      renderUrl,
      renderPath,
      renders: Array.isArray(renderHistory) ? renderHistory.map((r) => ({ ...r })) : [],
      viewMode,
      estimation: estimationCopy,
    };
    const payload = buildPayloadFromState(entry);
    const hash = JSON.stringify(payload);
    setIsSaving(true);
    setSaveError('');
    try {
      await api.patch(`/design/conversations/${convId}`, payload);
      lastSavedHashRef.current = hash;
    } catch (err) {
      console.error('[forceSaveConversation] error', err);
      setSaveError(err?.response?.data?.error || 'Failed to save conversation.');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }

  async function selectConversation(id) {
    if (!id || id === convId) {
      setShowHistory(false);
      cancelRename();
      return;
    }

    if (historyReadyRef.current && convId) {
      try {
        await forceSaveConversation();
      } catch (err) {
        console.warn('[selectConversation] forceSave failed', err);
      }
    }

    let convo = convs.find((x) => x.id === id);
    try {
      const { data } = await api.get(`/design/conversations/${id}`);
      if (data?.conversation) {
        convo = data.conversation;
      }
    } catch (err) {
      console.warn('[selectConversation] hydrate failed', err?.response?.data || err);
    }

    if (!convo) return;
    setConvId(convo.id || id);
    hydrateConversation(convo);
    setConvs((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((c) => c.id === (convo.id || id));
      const normalizedRenderUrl = isFullUrl(convo.renderUrl) ? convo.renderUrl : '';
      const nextEntry = {
        ...convo,
        id: convo.id || id,
        renderUrl: normalizedRenderUrl,
        updatedAt: convo.updatedAt || new Date().toISOString(),
      };
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...nextEntry };
      } else {
        list.unshift(nextEntry);
        if (list.length > MAX_HISTORY) list.length = MAX_HISTORY;
      }
      return list;
    });
    setShowHistory(false);
    cancelRename();
  }

  function cancelRename() {
    setRenamingId('');
    setRenameDraft('');
  }

  function startRename(convo) {
    if (!convo?.id) return;
    setRenamingId(convo.id);
    const initialTitle = (convo.title && convo.title.trim()) || (convo.id === convId ? convTitle : '');
    setRenameDraft(initialTitle);
  }

  async function saveConversationTitle(id, nextTitle) {
    if (!id) {
      cancelRename();
      return;
    }

    const trimmed = (nextTitle || '').trim().slice(0, 120) || 'New conversation';
    const isActive = id === convId;
    const existing = convs.find((c) => c.id === id);
    if (!existing && !isActive) {
      cancelRename();
      return;
    }

    const now = new Date().toISOString();
    const previousSnapshot = existing ? { ...existing } : null;

    setConvs((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      return list.map((c) => (c.id === id ? { ...c, title: trimmed, updatedAt: now } : c));
    });

    if (isActive) {
      const fallbackTitle = deriveTitle(messages);
      setConvTitle(trimmed);
      setTitleEdited(trimmed !== fallbackTitle);
    }

    const payload = isActive
      ? buildPayloadFromState({
          id: convId,
          title: trimmed,
          messages,
          materials,
          imageSummary,
          renderUrl,
          renderPath,
          renders: Array.isArray(renderHistory) ? renderHistory.map((r) => ({ ...r })) : [],
          viewMode,
          estimation,
        })
      : buildPayloadFromState({ ...existing, id, title: trimmed });

    setIsSaving(true);
    setSaveError('');
    try {
      const { data } = await api.patch(`/design/conversations/${id}`, payload);
      const updated = data?.conversation;
      if (updated) {
        setConvs((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          return list.map((c) => (c.id === id ? { ...c, ...updated } : c));
        });
        if (isActive) {
          setConvTitle(updated.title || trimmed);
          setTitleEdited(updated.title !== deriveTitle(messages));
        }
      }
      await reloadConversationList();
    } catch (err) {
      console.error('[renameConversation] error', err);
      setSaveError(err?.response?.data?.error || err?.message || 'Failed to rename conversation.');
      if (previousSnapshot) {
        setConvs((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          return list.map((c) => (c.id === id ? { ...c, ...previousSnapshot } : c));
        });
      }
    } finally {
      setIsSaving(false);
      cancelRename();
    }
  }

  async function newConversation() {
    const base = {
      title: 'New conversation',
      messages: [],
      materials: [],
      imageSummary: '',
      renderUrl: '',
      viewMode,
      estimation: null,
    };
    setIsSaving(true);
    setSaveError('');
    try {
      const { data } = await api.post('/design/conversations', base);
      const convo = data?.conversation;
      if (!convo) throw new Error('Conversation was not created.');
      setConvs((prev) => [convo, ...prev.filter((c) => c.id !== convo.id)].slice(0, MAX_HISTORY));
      setConvId(convo.id);
      hydrateConversation(convo);
      setShowHistory(false);
      setDraft('');
      clearRealtimeState();
    } catch (err) {
      console.error('[newConversation] error', err);
      setSaveError(err?.response?.data?.error || err.message || 'Failed to start a new conversation.');
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteConversation(id) {
    if (!id) return;
    if (renamingId === id) cancelRename();
    try {
      await api.delete(`/design/conversations/${id}`);
      setSaveError('');
    } catch (err) {
      console.error('[deleteConversation] error', err);
      setSaveError(err?.response?.data?.error || 'Failed to delete conversation.');
      return;
    }
    const remaining = convs.filter((c) => c.id !== id);
    setConvs(remaining);
    if (id === convId) {
      if (remaining[0]) {
        setConvId(remaining[0].id);
        hydrateConversation(remaining[0]);
      } else {
        setConvId('');
        await newConversation();
      }
    }
  }

  /* ------------------------- chat & image ------------------------- */
  const handleImageSelect = (file) => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImageFile(null);
      setImagePreview('');
    }
  };

  const analyzeImage = async () => {
    if (!imageFile || imageLoading) return;
    setImageLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', imageFile);
      const { data } = await api.post('/design/analyze-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const nextSummary = typeof data.summary === 'string' ? data.summary : '';
      const nextMaterials = Array.isArray(data.materials) ? data.materials : [];
      setImageSummary(nextSummary);
      setMaterialsSanitized(nextMaterials);
      toast.success('Image analyzed. Materials extracted.');

      // Clear the image file after analysis
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImageFile(null);
      setImagePreview('');

      // Scroll to materials section
      setTimeout(() => matTopRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to analyze image.');
    } finally {
      setImageLoading(false);
    }
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
  };

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  async function sendChat() {
    if (!draft.trim() || chatLoading) return;
    const newMsgs = [...messages, { role: 'user', content: draft.trim() }];
    setMessages(newMsgs);
    setDraft('');
    setChatLoading(true);
    setSaveError('');
    try {
      const trimmedImageSummary = imageSummaryForContext;
      const safeImageSummary =
        trimmedImageSummary.length > 1200
          ? `${trimmedImageSummary.slice(0, 1197)}...`
          : trimmedImageSummary;
      const imageMaterialsPayload = Array.isArray(materials)
        ? materials
            .slice(0, 40)
            .map((m) => ({
              item: (m?.item ?? '').toString(),
              quantity: (() => {
                if (typeof m?.quantity === 'number' && Number.isFinite(m.quantity)) {
                  return m.quantity;
                }
                if (typeof m?.quantity === 'string') {
                  const trimmed = m.quantity.trim();
                  if (!trimmed) return null;
                  const parsed = Number(trimmed);
                  return Number.isFinite(parsed) ? parsed : null;
                }
                return null;
              })(),
              unit: (m?.unit ?? '').toString(),
              notes: (m?.notes ?? '').toString(),
            }))
            .filter(
              (entry) =>
                entry.item ||
                Number.isFinite(entry.quantity) ||
                entry.unit ||
                entry.notes,
            )
        : [];
      const { data } = await api.post('/design/chat', {
        messages: newMsgs,
        imageSummary: safeImageSummary,
        imageMaterials: imageMaterialsPayload,
      });
      const reply = data.reply || 'OK.';
      const parsed = data.materials?.length ? data.materials : extractMaterialsFrom(reply);
      const assistantContent = formatAssistantReply(reply, parsed);
      setMessages([...newMsgs, { role: 'assistant', content: assistantContent }]);
      if (parsed?.length) {
        setMaterialsSanitized(parsed);
        setTimeout(() => matTopRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: 'Sorry, I hit a snag generating a reply.' },
      ]);
    } finally {
      setChatLoading(false);
    }
  }

  /* ------------------------- estimate (inline, no redirect) ------------------------- */
  async function estimateFromMaterials() {
    if (!materials.length || estimateLoading) return;

    const pricingMaterials = materials
      .map((m) => {
        const itemName = (m?.item || '').trim();
        if (!itemName) return null;
        const entry = { query: itemName };
        const note = (m?.notes || '').trim();
        if (note) entry.client_prompt = note;
        return entry;
      })
      .filter(Boolean);

    const chargeableCount = pricingMaterials.length;

    setEstimateLoading(true);
    setRealtimeError('');

    if (chargeableCount > 0) {
      const requestId =
        typeof window !== 'undefined' && window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `design-estimate-${Date.now()}`;
      try {
        const creditResult = await commitCredits({
          features: [{ feature: CREDIT_FEATURES.ESTIMATION_ITEM, quantity: chargeableCount }],
          requestId,
          metadata: { route: 'design-inline-estimate', items: chargeableCount },
        });
        if (creditResult && creditResult.available != null) {
          const nextAvailable = Number(creditResult.available);
          setBalance((prev) => ({ ...prev, available: Number.isFinite(nextAvailable) ? nextAvailable : prev.available ?? 0 }));
        }
      } catch (err) {
        setEstimateLoading(false);
        const shortfall = Number(err?.response?.data?.shortfall);
        if (shortfall > 0) {
          toast.error(`Not enough credits. You need ${shortfall} more.`);
        } else {
          const message = err?.response?.data?.error || err?.message || 'Unable to deduct credits for this estimate.';
          toast.error(message);
        }
        return;
      }
    }

    try {
      // 1) optional realtime prices (fast path for the table)
      try {
        if (pricingMaterials.length === 0) {
          clearRealtimeState();
        } else {
          const { data: realtime } = await api.post('/design/realtime-prices', {
            materials: pricingMaterials,
            conversationId: convId,
          });
          const normalizedItems = Array.isArray(realtime?.normalizedItems)
            ? realtime.normalizedItems
            : [];
          const realtimeResults = Array.isArray(realtime?.results) ? realtime.results : [];
          const fetchedAt =
            typeof realtime?.fetchedAt === 'string' && realtime.fetchedAt
              ? realtime.fetchedAt
              : new Date().toISOString();
          applyPriceResults(realtimeResults, {
            normalizedItems,
            totalRequested: realtime?.totalRequested ?? materials.length,
            fetchedAt,
          });
        }
      } catch (err) {
        const msg = err?.response?.data?.error || err.message || 'Live price lookup failed.';
        setRealtimeError(msg);
        console.error('[design realtime-prices]', msg);
      }

      // 2) generate CSV on server and send to /api/estimates/from-csv
      const csvBlob = await api
        .post('/design/materials-to-csv', { materials }, { responseType: 'blob' })
        .then((r) => r.data);
      const csvFile = new File([csvBlob], 'materials.csv', { type: 'text/csv' });

      const fd = new FormData();
      // Server expects "csv" (your controller supports this)
      fd.append('csv', csvFile);
      const resp = await api.post('/estimates/from-csv', fd);

      // 3) stamp + set in state
      const estimationData = resp?.data || {};
      const stampedEstimation = {
        savedAt: new Date().toISOString(),
        data: estimationData,
      };
      setEstimation(stampedEstimation);

      // 4) persist just the estimation to the conversation (no page change)
      if (convId) {
        try {
          setIsSaving(true);
          setSaveError('');
          await api.post(`/design/conversations/${convId}/estimation`, {
            estimation: stampedEstimation,
          });
        } catch (err) {
          console.warn('[estimateFromMaterials] failed to persist estimation', err);
          setSaveError(err?.response?.data?.error || err?.message || 'Failed to save estimation.');
        } finally {
          setIsSaving(false);
        }
      }

      // 5) local cache for fast reload
      try {
        localStorage.setItem('design_last_estimation', JSON.stringify(stampedEstimation));
        localStorage.setItem('last_estimate_result', JSON.stringify(estimationData));
      } catch {}
    } catch (e) {
      const errorMessage =
        e?.response?.data?.error ||
        e?.message ||
        'The estimator service is unavailable right now.';
      const failureEstimation = {
        savedAt: new Date().toISOString(),
        data: {
          error: errorMessage,
          status: e?.response?.status || null,
        },
      };
      const failureMessages = [
        ...messages,
        {
          role: 'assistant',
          content:
            "I couldn't send the list to the estimator. Please make sure the estimator endpoint is available.",
        },
      ];
      setMessages(failureMessages);
      setEstimation(failureEstimation);

      if (convId) {
        const immediatePayload = buildPayloadFromState({
          id: convId,
          title: convTitle?.trim() || deriveTitle(failureMessages),
          messages: failureMessages,
          materials,
          imageSummary,
          renderUrl,
          renderPath,
          renders: Array.isArray(renderHistory) ? renderHistory.map((r) => ({ ...r })) : [],
          viewMode,
          estimation: failureEstimation,
        });
        try {
          setIsSaving(true);
          setSaveError('');
          await api.patch(`/design/conversations/${convId}`, immediatePayload);
          lastSavedHashRef.current = JSON.stringify(immediatePayload);
        } catch (err) {
          console.warn('[estimateFromMaterials] failed to persist failed estimation', err);
          setSaveError(err?.response?.data?.error || err?.message || 'Failed to save conversation.');
        } finally {
          setIsSaving(false);
        }
      }
    } finally {
      setEstimateLoading(false);
    }
  }

  function estimationPreviewText(est) {
    if (!est || typeof est !== 'object') return 'Estimation saved for this conversation.';
    const data = est.data;
    if (data && typeof data === 'object') {
      if (typeof data.summary === 'string' && data.summary.trim()) return data.summary.trim();
      if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
      if (Array.isArray(data.items) && data.items.length) {
        return `Saved ${data.items.length} line item${data.items.length === 1 ? '' : 's'} from the last estimation.`;
      }
      if (Array.isArray(data.results) && data.results.length) {
        return `Saved ${data.results.length} priced suggestion${data.results.length === 1 ? '' : 's'}.`;
      }
      const keys = Object.keys(data);
      if (keys.length) return `Saved fields: ${keys.slice(0, 5).join(', ')}`;
    }
    return 'Estimation saved for this conversation.';
  }

  function downloadEstimation() {
    if (!estimation?.data) return;
    try {
      if (typeof window === 'undefined') return;
      const blob = new Blob([JSON.stringify(estimation.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const stamp = sanitizeFileName(estimation.savedAt, String(Date.now()));
      anchor.download = `estimation-${stamp}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.warn('[downloadEstimation] failed to export estimation', err);
    }
  }

async function generateGuide() {
  if (generatingGuide || !materials.length) return;
  setGeneratingGuide(true);
  setGuideUrl('');
  setGuideTitle('');
  setShowGuidePreview(false);
  try {
    const { goal, brief } = buildRenderPayload();

    if (!convId) {
      throw new Error('No active conversation to generate from.');
    }

    try {
      await forceSaveConversation();
    } catch (saveErr) {
      console.warn('[generateGuide] force save failed', saveErr);
    }

    // Include recent chat, image summary, and estimation highlights for richer context
    const context = messages.slice(-8);
    const estimationLite = estimation?.data ? {
      items: Array.isArray(estimation.data.items) ? estimation.data.items.slice(0, 100) : undefined,
      summary: estimation.data.summary || estimation.data.message
    } : undefined;

    const { data } = await api.post('/design/instructions', {
      goal,
      brief,
      materials,
      conversationId: convId || undefined,
      context,
      imageSummary: imageSummaryForContext,
      estimation: estimationLite
    });

    if (data?.url) setGuideUrl(data.url);
    else if (data?.inlineBase64) setGuideUrl(`data:application/pdf;base64,${data.inlineBase64}`);
    else setGuideUrl('');

    setGuideTitle(data?.title || '');
    if (convId && data?.url) {
      try {
        await api.patch(`/design/conversations/${convId}`, {
          lastGuide: { url: data.url, title: data?.title || '', savedAt: new Date().toISOString() }
        });
      } catch {}
    }
  } catch (e) {
    setMessages((m) => [...m, { role: 'assistant', content: 'Guide generation failed.' }]);
  } finally {
    setGeneratingGuide(false);
  }
}

  /* ------------------------- visualize build ------------------------- */
  async function visualizeBuild() {
    if (rendering) return;
    if (renderQuote.shortfall > 0) {
      toast.error(`Not enough credits. You need ${'$'}{renderQuote.shortfall} more.`);
      return;
    }
    if (renderQuote.loading) {
      toast.info('Calculating credit requirement, please wait...');
      return;
    }

    setRendering(true);
    setRenderUrl('');
    const requestId = generateRenderRequestId();
    renderCreditRequestRef.current = requestId;
    try {
      const { goal, brief } = buildRenderPayload();
      const activeConversationId =
        convId ||
        (Array.isArray(convs) ? convs.find((c) => c?.id)?.id : undefined) ||
        undefined;

      const payload = {
        goal,
        brief,
        materials,
        context: messages.slice(-8),
        aspect: brief?.view || 'landscape',
        style: brief?.style || undefined,
        environment: brief?.environment || undefined,
        creditRequestId: requestId,
      };
      if (activeConversationId) {
        payload.conversationId = activeConversationId;
      }
      const response = await api.post('/design/render-final', payload);
      const data = response.data || {};
      const balanceHeader = Number(response.headers?.['x-credit-balance']);
      if (Number.isFinite(balanceHeader)) {
        setBalance((prev) => ({ ...prev, available: balanceHeader }));
      }
      setRenderQuote((prev) => ({
        ...prev,
        loading: false,
        shortfall: 0,
        error: '',
        available: Number.isFinite(balanceHeader) ? balanceHeader : prev.available,
      }));

      const { imageUrl, storage, spec } = data;
      if (storage?.path) {
        const storedAt = new Date().toISOString();
        const bytes = Number.isFinite(storage?.bytes) ? Number(storage.bytes) : undefined;
        setRenderPath(storage.path);
        setRenderHistory((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          const entry = { path: storage.path, bytes, createdAt: storedAt };
          if (isFullUrl(imageUrl)) entry.url = imageUrl;
          next.push(entry);
          if (next.length > 50) next.splice(0, next.length - 50);
          return next;
        });
      }
      if (spec?.extraNotes && !imageSummary) {
        setImageSummary(spec.extraNotes);
      }

      if (isFullUrl(imageUrl)) {
        setRenderUrl(imageUrl);
      } else {
        setRenderUrl('');
        setMessages((m) => [...m, { role: 'assistant', content: 'Render did not return an image.' }]);
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        const details = err.response?.data || {};
        const shortfall = Math.max(0, Number(details.shortfall ?? 0));
        const available = Number(details.available ?? creditBalance?.available ?? renderQuote.available ?? 0);
        setRenderQuote((prev) => ({
          ...prev,
          loading: false,
          shortfall,
          error: details.error || 'Not enough credits to visualize the build.',
          available,
        }));
        if (Number.isFinite(available)) {
          setBalance((prev) => ({ ...prev, available }));
        }
        toast.error(details.error || 'Not enough credits to visualize the build.');
      } else {
        console.error('[visualizeBuild]', err);
        setRenderQuote((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || 'Preview render failed.',
        }));
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: 'Preview render failed (render-final route?).' },
        ]);
      }
    } finally {
      setRendering(false);
      renderCreditRequestRef.current = '';
      refreshBalance().catch(() => {});
    }
  }

  // Build table rows: one per material, even if no realtime match
  const normalizeMaterialToQuery = (m) => {
    const itemKey = normalizeQueryKey(sanitizeMaterialField(m?.item));
    if (itemKey) return itemKey;
    const fallback = [sanitizeMaterialField(m?.quantity), sanitizeMaterialField(m?.unit)].filter(Boolean).join(' ');
    return normalizeQueryKey(fallback);
  };

    const priceByQuery = new Map();
  (priceResults || []).forEach((entry) => {
    if (!entry) return;
    const keys = new Set();
    if (entry.queryKey) keys.add(entry.queryKey);
    const primaryCandidate = entry.candidates?.[entry.selectedIndex] || entry.candidates?.[0];
    [entry.query, primaryCandidate?.query, primaryCandidate?.title].forEach((value) => {
      const key = normalizeQueryKey(value);
      if (key) keys.add(key);
    });
    entry.candidates?.forEach((candidate) => {
      [candidate.query, candidate.title].forEach((value) => {
        const key = normalizeQueryKey(value);
        if (key) keys.add(key);
      });
    });
    keys.forEach((key) => {
      if (key && !priceByQuery.has(key)) {
        priceByQuery.set(key, entry);
      }
    });
  });

  const realtimeDisplayRows = (materials || []).map((m, idx) => {
    const itemName = (m?.item || '').trim();
    const queryKey = normalizeMaterialToQuery(m);
    const quantityValue = parseQuantityValue(m?.quantity);
    const quantityDisplay = Number.isFinite(quantityValue)
      ? (Number.isInteger(quantityValue) ? String(quantityValue) : String(Number(quantityValue.toFixed(2))))
      : '-';

    let entry = priceByQuery.get(queryKey);
    if (!entry && itemName) {
      entry = priceByQuery.get(normalizeQueryKey(itemName));
    }

    const rowKey =
      sanitizeStateKey(entry?.queryKey) ||
      sanitizeStateKey(queryKey) ||
      sanitizeStateKey(itemName) ||
      `row-${idx}`;
    const candidates = entry?.candidates || [];
    const selectedIndex = entry?.selectedIndex ?? 0;
    const selectedCandidate = candidates[selectedIndex] || null;

    const itemLabel = itemName || selectedCandidate?.query || entry?.query || '-';
    const productTitle = selectedCandidate?.title && selectedCandidate.title !== itemLabel ? selectedCandidate.title : '';

    const priceValue = selectedCandidate ? parsePriceValue(selectedCandidate.price) : null;
    const priceDisplay = selectedCandidate?.price || (Number.isFinite(priceValue) ? formatCurrencyValue(priceValue) : '-');

    let totalPrice = null;
    let totalPriceDisplay = '-';
    if (Number.isFinite(priceValue) && Number.isFinite(quantityValue)) {
      totalPrice = priceValue * quantityValue;
      totalPriceDisplay = formatCurrencyValue(totalPrice);
    }

    const source = selectedCandidate?.source || entry?.source || '';
    const url = sanitizeExternalUrl(selectedCandidate?.url || selectedCandidate?.product_link || '');
    const productLink = sanitizeExternalUrl(selectedCandidate?.product_link || selectedCandidate?.url || '');

    return {
      rowKey,
      entryQueryKey: sanitizeStateKey(entry?.queryKey) || rowKey,
      query: entry?.query || itemName || queryKey || '-',
      itemLabel,
      productTitle,
      quantityDisplay,
      quantityValue,
      priceDisplay,
      priceValue,
      totalPrice,
      totalPriceDisplay,
      source,
      url: url || null,
      product_link: productLink || url || null,
      candidates,
      selectedIndex,
      hasAlternatives: candidates.length > 1,
    };
  });

  const totalCost = realtimeDisplayRows.reduce((sum, row) => (
    Number.isFinite(row.totalPrice) ? sum + row.totalPrice : sum
  ), 0);
  const showTotalCost = realtimeDisplayRows.some((row) => Number.isFinite(row.totalPrice));
  const totalCostDisplay = showTotalCost ? formatCurrencyValue(totalCost) : null;

  /* ------------------------- UI ------------------------- */
  const guideDownloadName = guideTitle ? `${slugify(guideTitle)}.pdf` : undefined;
  const estimateChargeableItems = materials.filter((m) => ((m?.item || '').trim()).length > 0).length;
  const estimateCreditsNeeded = estimateChargeableItems;
  const estimateBalanceKnown = creditsLoaded && typeof creditBalance?.available === 'number';
  const estimateBalanceAvailable = estimateBalanceKnown ? Number(creditBalance?.available ?? 0) : null;
  const estimateShortfall = estimateBalanceKnown
    ? Math.max(0, estimateCreditsNeeded - Number(estimateBalanceAvailable ?? 0))
    : 0;
  const estimateCostLabel = estimateCreditsNeeded > 0
    ? (estimateBalanceKnown
        ? `Estimate This costs ${estimateCreditsNeeded} credit${estimateCreditsNeeded === 1 ? '' : 's'}. Available: ${estimateBalanceAvailable}.`
        : `Estimate This costs ${estimateCreditsNeeded} credit${estimateCreditsNeeded === 1 ? '' : 's'} (checking balance...)`)
    : 'Estimate This costs 0 credits. Add at least one item to estimate.';
  const disableEstimate = !materials.length || estimateLoading || (estimateBalanceKnown && estimateShortfall > 0);

  const renderCreditsNeeded = renderQuote.totalCost || 3;
  const disableRender = rendering || renderQuote.loading || renderQuote.shortfall > 0;
  const renderBalanceAvailable = creditBalance?.available ?? renderQuote.available ?? 0;

  return (
    <>
    <div className="min-h-screen overflow-x-hidden text-zinc-200" style={pageBgStyle}>
      <style>{sliderKeyframes}</style>
      <div className="flex flex-col min-h-screen px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <div className={`grid grid-cols-1 gap-4 sm:gap-6 ${hasMaterials ? 'lg:grid-cols-12' : ''} min-h-[calc(100vh-220px)] items-start`}>
          {/* Chat */}
          <section
            className={`${card} p-4 sm:p-6 ${hasMaterials ? 'lg:order-1' : ''} ${
              hasMaterials
                ? (wideTable ? 'lg:col-span-8 xl:col-span-8' : 'lg:col-span-8 xl:col-span-8')
                : 'lg:col-span-12'
            } relative flex flex-col ${hasMaterials ? 'lg:sticky lg:top-6 lg:self-start max-h-[calc(100vh-140px)]' : ''}`}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div />
              <div className="flex flex-wrap items-center justify-end gap-2">
                <div className="min-w-[160px] text-xs sm:text-sm">
                  <label className="sr-only" htmlFor="conversation-select">Conversation history</label>
                  <select
                    id="conversation-select"
                    className="w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-2 text-xs sm:text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-60"
                    value={convId || ''}
                    onChange={async (e) => {
                      const next = e.target.value;
                      if (next) await selectConversation(next);
                    }}
                    disabled={historyLoading || !convs.length}
                  >
                    {convs.length === 0 && (
                      <option value="" className="bg-zinc-900 text-zinc-200">
                        {historyLoading ? 'Loading conversations...' : 'No conversations yet'}
                      </option>
                    )}
                    {convs.length > 0 &&
                      convs.map((c) => (
                        <option key={c.id} value={c.id} className="bg-zinc-900 text-zinc-200">
                          {c.title || 'Untitled'}
                        </option>
                      ))}
                  </select>
                </div>
                <button className={action} onClick={newConversation} disabled={isSaving || historyLoading}>
                  New
                </button>
                <button className={action} onClick={() => setMessages([])}>
                  Clear
                </button>
                <button className={action} onClick={() => setShowHistory((s) => !s)}>
                  Manage
                </button>
              </div>
            </div>

            {(historyLoading || isSaving || historyError || saveError) && (
              <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                {historyLoading && <span>Loading history...</span>}
                {isSaving && !historyLoading && <span>Saving...</span>}
                {!isSaving && saveError && (
                  <span className="flex items-center gap-2 text-red-300">
                    {saveError}
                    <button
                      className="rounded border border-red-400 px-2 py-1 text-[10px] text-red-200 hover:bg-red-400/10"
                      onClick={() => forceSaveConversation().catch(() => {})}
                    >
                      Retry save
                    </button>
                  </span>
                )}
                {!historyLoading && historyError && !saveError && (
                  <span className="text-red-300">{historyError}</span>
                )}
              </div>
            )}
            {showHistory && (
              <div className={`${subPanel} absolute right-2 top-12 z-20 w-[calc(100vw-2rem)] sm:w-80 max-h-[60vh] overflow-y-auto p-2`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wide text-zinc-400">Conversations</div>
                  <button className={action} onClick={() => setShowHistory(false)}>
                    Close
                  </button>
                </div>
                <div className="divide-y divide-white/10">
                  {historyLoading && <div className="p-3 text-sm text-zinc-400">Loading...</div>}
                  {!historyLoading && historyError && (
                    <div className="p-3 text-sm text-red-300">{historyError}</div>
                  )}
                  {!historyLoading && (!convs || convs.length === 0) && (
                    <div className="p-3 text-sm text-zinc-400">No conversations yet.</div>
                  )}
                  {convs.map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-center gap-2 p-2 ${c.id === convId ? 'bg-white/5' : ''}`}
                    >
                      {renamingId === c.id ? (
                        <form
                          className="flex w-full items-center gap-2"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            await saveConversationTitle(c.id, renameDraft);
                          }}
                        >
                          <input
                            ref={renameInputRef}
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelRename();
                              }
                            }}
                            maxLength={120}
                            placeholder="Conversation title"
                            className="flex-1 rounded-md border border-white/20 bg-black/60 px-2 py-1 text-xs text-zinc-100 focus:border-amber-300 focus:outline-none" disabled={isSaving && renamingId === c.id}
                          />
                          <button type="submit" className={`${action} px-3 py-1 text-[11px]`} disabled={isSaving && renamingId === c.id}>
                            Save
                          </button>
                          <button
                            type="button"
                            className={`${action} px-3 py-1 text-[11px]`}
                            onClick={cancelRename}
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <>
                          <button onClick={() => selectConversation(c.id)} className="flex-1 text-left">
                            <div className="text-sm text-zinc-100 truncate">{c.title || 'Untitled'}</div>
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                              {new Date(c.updatedAt || c.createdAt || Date.now()).toLocaleString()}
                            </div>
                          </button>
                          <button
                            type="button"
                            className="text-xs text-amber-300 hover:text-amber-200"
                            onClick={() => startRename(c)}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteConversation(c.id)}
                            className="text-xs text-red-300 hover:text-red-200"
                            title="Delete"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2 sm:space-y-3 flex-1 overflow-y-auto pr-1 sm:pr-2">
              {messages.length === 0 && !chatLoading && (
                <div className="flex items-center justify-center h-full min-h-[200px]">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-semibold bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent mb-2">
                      What would you like to build today?
                    </div>
                    <p className="text-sm sm:text-base text-zinc-400">
                      Describe your project or attach an image to get started
                    </p>
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={m.role === 'assistant' ? bubbleAssistant : bubbleUser}
                >
                  <div className="whitespace-pre-wrap break-words text-[15px] sm:text-sm leading-relaxed">
                    {m.content}
                  </div>
                </div>
              ))}
              {chatLoading && <div className="text-sm opacity-70">Thinking…</div>}
            </div>

            <div className="mt-4 sm:mt-6 rounded-2xl sm:rounded-[28px] border border-transparent bg-gradient-to-br from-[#0b0912]/88 via-[#0a0810]/86 to-[#08070c]/90 shadow-[0_22px_60px_rgba(0,0,0,0.55)]">
              <div className="px-3 sm:px-5 pt-3 sm:pt-5 pb-3 sm:pb-4">
                {imagePreview && (
                  <div className="mb-3 relative rounded-xl overflow-hidden border border-amber-400/40 bg-amber-500/10">
                    <div className="relative w-full" style={{ paddingTop: '40%' }}>
                      <img
                        src={imagePreview}
                        alt="Attached preview"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 p-2 border-t border-amber-400/30">
                      <span className="text-xs text-amber-100">Ready to analyze</span>
                      <div className="flex gap-2">
                        <button
                          onClick={analyzeImage}
                          className="text-xs px-2.5 sm:px-3 py-1.5 rounded-lg bg-amber-500 text-zinc-900 font-semibold hover:bg-amber-400 transition disabled:opacity-50"
                          disabled={imageLoading}
                        >
                          {imageLoading ? 'Analyzing...' : 'Analyze'}
                        </button>
                        <button
                          onClick={removeImage}
                          className="text-xs px-2.5 sm:px-3 py-1.5 rounded-lg border border-white/20 text-zinc-200 hover:bg-white/10 transition"
                          disabled={imageLoading}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <textarea
                  className="w-full min-h-[60px] sm:min-h-[80px] max-h-[300px] resize-none rounded-xl sm:rounded-2xl bg-transparent border border-transparent px-2 py-1 text-base sm:text-lg text-white placeholder:text-white/35 outline-none focus:animate-[focusFlash_500ms_ease-out]"
                  placeholder="Ask build assistant a question..."
                  value={draft}
                  disabled={chatLoading || historyLoading}
                  autoCapitalize="off"
                  autoCorrect="off"
                  onChange={(e) => {
                    setDraft(e.target.value);
                    // Auto-expand textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  style={{ height: 'auto' }}
                />
              </div>
              <div className="border-t border-transparent px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageSelect(e.target.files?.[0] || null)}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl sm:rounded-2xl border border-white/20 bg-white/5 px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-zinc-200 hover:border-amber-300/60 hover:bg-white/10 transition disabled:opacity-60 flex-shrink-0"
                  disabled={chatLoading || historyLoading || imageLoading}
                  title="Attach image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span className="hidden sm:inline">Attach Image</span>
                </button>
                <button
                  onClick={sendChat}
                  className="rounded-xl sm:rounded-2xl bg-white px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold text-zinc-900 shadow-[0_10px_40px_rgba(255,255,255,0.18)] transition hover:shadow-[0_14px_48px_rgba(255,255,255,0.25)] disabled:opacity-60 flex-shrink-0"
                  disabled={!draft.trim() || chatLoading || historyLoading}
                >
                  Send
                </button>
              </div>
            </div>

            {/* Idea Bubbles - Only show when no messages */}
            {messages.length === 0 && !chatLoading && (
              <div className="mt-4 flex flex-wrap gap-2 sm:gap-3 justify-center">
                <button
                  onClick={() => {
                    setDraft("Build a backyard fire pit");
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-xl px-4 py-2.5 text-sm text-white shadow-[0_8px_32px_rgba(0,0,0,0.4)] hover:bg-black/60 hover:border-white/20 hover:shadow-[0_12px_40px_rgba(0,0,0,0.6)] transition-all"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                  </svg>
                  <span className="italic">"Build a backyard fire pit"</span>
                </button>
                <button
                  onClick={() => {
                    setDraft("Design a 10x12 outdoor patio");
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-xl px-4 py-2.5 text-sm text-white shadow-[0_8px_32px_rgba(0,0,0,0.4)] hover:bg-black/60 hover:border-white/20 hover:shadow-[0_12px_40px_rgba(0,0,0,0.6)] transition-all"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span className="italic">"Design a 10x12 outdoor patio"</span>
                </button>
              </div>
            )}
          </section>
          {/* Image / Materials / Render */}
          {hasMaterials && (
          <section
            className={`${card} p-4 sm:p-6 lg:order-2 ${
              wideTable ? 'lg:col-span-4 xl:col-span-4' : 'lg:col-span-4 xl:col-span-4'
            } flex flex-col h-full max-h-none overflow-hidden`}
          >
            <header className="mb-3 flex items-center justify-between gap-2">
              <div />
            </header>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1 sm:pr-2">
            {renderPreviewBlock}

            {/* Materials toolbar */}
            {priceResults.length === 0 && (
            <div ref={matTopRef} className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-lg sm:text-base">Materials ({materials.length})</h3>
                  <span className="text-xs text-zinc-400">Table view</span>
                </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className={action}
                  onClick={() => setMaterials((p) => {
                    const next = Array.isArray(p) ? [...p] : [];
                    next.push(sanitizeMaterialRecord({ item: '', quantity: '', unit: '', notes: '' }));
                    return next;
                  })}
                >
                  Add Row
                </button>
                <button className={action} onClick={() => setMaterials([])} disabled={!materials.length}>
                  Clear
                </button>
                <span className="text-xs text-zinc-400">{estimateCostLabel}</span>
                {estimateBalanceKnown && estimateShortfall > 0 && (
                  <span className="text-xs text-red-400">
                    Need {estimateShortfall} more credit{estimateShortfall === 1 ? '' : 's'}.
                  </span>
                )}
                <button
                  onClick={estimateFromMaterials}
                  disabled={disableEstimate}
                  className={primary}
                >
                  Estimate This
                </button>
              </div>
            </div>
            )}

            {/* CARDS (mobile-friendly) */}
            {priceResults.length === 0 && (
            <div className="hidden">
              {materials.length === 0 && (
                <div className="p-3 text-zinc-400 text-sm border border-white/10 rounded-xl">
                  No materials yet. Try the chat or upload an image.
                </div>
              )}
              {materials.map((m, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-black/40 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <label className="text-xs uppercase tracking-wide text-zinc-400">Item</label>
                      <button
                        onClick={() => {
                          const n = materials.slice();
                          n.splice(i, 1);
                          setMaterialsSanitized(n);
                        }}
                        className="text-red-300 hover:text-red-200 text-sm"
                        title="Remove"
                      >
                      Remove
                    </button>
                  </div>
                  <input
                    className="mt-1 w-full rounded-lg bg-transparent border border-white/10 px-3 py-2 text-base outline-none placeholder:text-zinc-500"
                    placeholder="e.g., 2x6 composite deck board"
                    value={m.item || ''}
                    autoCapitalize="off"
                    autoCorrect="off"
                    onChange={(e) => updateMaterialField(i, 'item', e.target.value)}
                  />
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-zinc-400">Qty</label>
                      <input
                        inputMode="decimal"
                        className="mt-1 w-full rounded-lg bg-transparent border border-white/10 px-3 py-2 text-base outline-none"
                        placeholder="24"
                        value={m.quantity || ''}
                        onChange={(e) => updateMaterialField(i, 'quantity', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-zinc-400">Unit</label>
                      <input
                        className="mt-1 w-full rounded-lg bg-transparent border border-white/10 px-3 py-2 text-base outline-none"
                        placeholder="ea / ft / box"
                        value={m.unit || ''}
                        autoCapitalize="off"
                        autoCorrect="off"
                        onChange={(e) => updateMaterialField(i, 'unit', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-zinc-400">Notes</label>
                      <input
                        className="mt-1 w-full rounded-lg bg-transparent border border-white/10 px-3 py-2 text-base outline-none"
                        placeholder="12 ft boards, slate"
                        value={m.notes || ''}
                        autoCapitalize="off"
                        autoCorrect="off"
                        onChange={(e) => updateMaterialField(i, 'notes', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}

            {priceResults.length === 0 && estimateLoading && (
              <div className="mt-4 space-y-2">
                <div className="h-1.5 overflow-hidden rounded-full bg-amber-400/20">
                  <div
                    className="h-full w-1/2 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600"
                    style={sliderAnimationStyle}
                  />
                </div>
                <p className="text-xs text-amber-200/80">Not seeing expected results? Try adjusting the item name.</p>
              </div>
            )}

            {/* TABLE (desktop-friendly) */}
            {priceResults.length === 0 && (
            <div className="rounded-xl border border-white/10">
              <div className="overflow-x-auto">
                <table className="min-w-[660px] w-full text-sm">
                  <colgroup>
                    <col className="w-[55%]" />
                    <col className="w-[12%]" />
                    <col className="w-[14%]" />
                    <col className="w-[17%]" />
                    <col className="w-[2%]" />
                  </colgroup>

                  <thead className="bg-white/10">
                    <tr>
                      <th className="text-left p-2">Item</th>
                      <th className="text-left p-2">Qty</th>
                      <th className="text-left p-2">Unit</th>
                      <th className="text-left p-2">Notes</th>
                      <th className="p-2 w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {materials.map((m, i) => (
                      <tr key={i} className="border-t border-white/10">
                        <td className="p-2 align-top">
                          <input
                            className="w-full bg-transparent outline-none placeholder:text-zinc-500"
                            placeholder="e.g., 2x6 composite deck board"
                            value={m.item || ''}
                            onChange={(e) => updateMaterialField(i, 'item', e.target.value)}
                          />
                        </td>
                        <td className="p-2 align-top min-w-[90px]">
                          <input
                            className="w-full bg-transparent outline-none"
                            placeholder="24"
                            value={m.quantity || ''}
                            onChange={(e) => updateMaterialField(i, 'quantity', e.target.value)}
                          />
                        </td>
                        <td className="p-2 align-top min-w-[110px]">
                          <input
                            className="w-full bg-transparent outline-none"
                            placeholder="ea / ft / box"
                            value={m.unit || ''}
                            onChange={(e) => updateMaterialField(i, 'unit', e.target.value)}
                          />
                        </td>
                        <td className="p-2 align-top">
                          <input
                            className="w-full bg-transparent outline-none"
                            placeholder="12 ft boards, slate"
                            value={m.notes || ''}
                            onChange={(e) => updateMaterialField(i, 'notes', e.target.value)}
                          />
                        </td>
                        <td className="p-2 text-right align-top">
                          <button
                            onClick={() => {
                              const n = materials.slice();
                              n.splice(i, 1);
                              setMaterialsSanitized(n);
                            }}
                            className="text-red-300 hover:text-red-200"
                            title="Remove"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}

                    {!materials.length && (
                      <tr>
                        <td className="p-3 text-white/60" colSpan={5}>
                          No materials yet. Try the chat or upload an image.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {realtimeError && (
              <div className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {realtimeError}
              </div>
            )}

            {priceResults.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-xl p-3 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-white">Realtime Price Suggestions</span>
                    <span className="text-xs text-white/60">
                      Top {Math.min(realtimeDisplayRows.length, PRICE_ROWS_TO_SHOW)} shown
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-[520px] w-full text-sm">
                      <thead className="text-left text-white/80 bg-black/20">
                        <tr>
                          <th className="pb-2 pr-2">Item</th>
                          <th className="pb-2 pr-2">Quantity</th>
                          <th className="pb-2 pr-2">Price</th>
                          <th className="pb-2 pr-2">Total Price</th>
                          <th className="pb-2 pr-2">Source</th>
                          <th className="pb-2">URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {realtimeDisplayRows.slice(0, PRICE_ROWS_TO_SHOW).map((row, idx) => {
                          const linkHref = sanitizeExternalUrl(row?.url || row?.product_link || '');
                          return (
                            <Fragment key={row.rowKey || idx}>
                              <tr className="border-t border-white/10">
                                <td className="py-2 pr-2 align-top text-white/70">
                                  <div className="flex flex-col gap-1">
                                    <div className="flex items-start justify-between gap-2">
                                      <span className="font-medium text-white">{row.itemLabel || '-'}</span>
                                      {row.hasAlternatives && (
                                        <button
                                          type="button"
                                          onClick={() => toggleRowExpanded(row.entryQueryKey)}
                                          className="text-xs text-white/60 hover:text-white underline-offset-2 hover:underline"
                                        >
                                          {expandedRows[row.entryQueryKey] ? 'Hide options' : `Options (${row.candidates.length})`}
                                        </button>
                                      )}
                                    </div>
                                    {row.productTitle && (
                                      <div className="text-xs text-white/50">{row.productTitle}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 pr-2 align-top text-white/70">
                                  {row.quantityDisplay ?? '-'}
                                </td>
                                <td className="py-2 pr-2 align-top text-white text-right">{row.priceDisplay || '-'}</td>
                                <td className="py-2 pr-2 align-top text-white text-right">{row.totalPriceDisplay || '-'}</td>
                                <td className="py-2 pr-2 align-top text-white/60">{row.source || '-'}</td>
                                <td className="py-2 align-top">
                                  {linkHref ? (
                                    <a
                                      href={linkHref}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-white/60 hover:text-white"
                                    >
                                      Open
                                    </a>
                                  ) : (
                                    <span className="text-white/30">-</span>
                                  )}
                                </td>
                              </tr>
                              {expandedRows[row.entryQueryKey] && row.hasAlternatives && (
                                <tr className="border-t border-white/10 bg-black/40">
                                  <td colSpan={6} className="py-3">
                                    <div className="space-y-2">
                                      {row.candidates.map((candidate, candidateIdx) => {
                                        const isSelected = candidateIdx === row.selectedIndex;
                                        const candidatePriceValue = parsePriceValue(candidate.price);
                                        const candidatePriceDisplay = candidate.price || (Number.isFinite(candidatePriceValue) ? formatCurrencyValue(candidatePriceValue) : '-');
                                        const candidateSource = candidate.source || candidate.vendor || '';
                                        const candidateUrl = sanitizeExternalUrl(candidate.url || candidate.product_link || '');
                                        return (
                                          <div
                                            key={`${row.entryQueryKey}-candidate-${candidateIdx}`}
                                            className={`flex flex-col gap-2 rounded-lg border px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${isSelected ? 'border-white/30 bg-white/10' : 'border-white/10 bg-black/30'}`}
                                          >
                                            <div className="flex-1 min-w-0">
                                              <div className="text-sm font-semibold text-white truncate">
                                                {candidate.title || candidate.query || 'Option'}
                                              </div>
                                              <div className="text-xs text-white/60">
                                                {candidateSource ? `Source: ${candidateSource}` : 'Source unavailable'}
                                              </div>
                                            </div>
                                            <div className="mt-2 flex shrink-0 items-center gap-3 sm:mt-0">
                                              <span className="text-sm text-white">
                                                {candidatePriceDisplay || '-'}
                                              </span>
                                              {candidateUrl ? (
                                                <a
                                                  href={candidateUrl}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="text-white/60 hover:text-white text-sm"
                                                >
                                                  Open
                                                </a>
                                              ) : (
                                                <span className="text-white/30 text-sm">-</span>
                                              )}
                                              {isSelected ? (
                                                <span className="text-xs uppercase font-semibold text-white/80">Selected</span>
                                              ) : (
                                                <button
                                                  type="button"
                                                  className={action}
                                                  onClick={() => handleCandidateSelect(row.entryQueryKey, candidateIdx)}
                                                >
                                                  Select
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}

                        {totalCostDisplay && (
                          <tr className="border-t border-white/30 font-semibold text-white">
                            <td className="py-2 pr-2 align-top" colSpan={2}>Total Cost</td>
                            <td className="py-2 pr-2 align-top text-right"></td>
                            <td className="py-2 pr-2 align-top text-right">{totalCostDisplay}</td>
                            <td className="py-2 pr-2 align-top"></td>
                            <td className="py-2 align-top"></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {showSavedEstimationCard && estimation && (
              <div className="mt-3 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-amber-200">Last Saved Estimation</span>
                  <span className="text-xs text-amber-200/70">
                    {estimation.savedAt ? new Date(estimation.savedAt).toLocaleString() : 'Saved'}
                  </span>
                </div>
                <div className="text-xs text-amber-100/80 whitespace-pre-wrap leading-relaxed">
                  {estimationPreviewText(estimation)}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    className={action}
                    onClick={() => setShowEstimateDetails((s) => !s)}
                  >
                    {showEstimateDetails ? 'Hide Details' : 'Show Details'}
                  </button>
                  <button className={action} onClick={loadSavedIntoPriceTable}>
                    Load Into Table
                  </button>
                  <button className={action} onClick={downloadEstimation}>
                    Download JSON
                  </button>
                </div>
                {showEstimateDetails && (
                  <div className="mt-3 rounded-lg border border-amber-400/20 bg-black/30 p-3 max-h-64 overflow-auto text-xs">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(estimation.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            {/* Render preview */}
            <div className="mt-4 space-y-2">
              <div className="flex flex-col gap-2 text-sm text-zinc-400 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {renderQuote.loading
                    ? 'Visualize Build costs 3 credits (checking balance...)'
                    : `Visualize Build costs ${renderCreditsNeeded} credit${renderCreditsNeeded === 1 ? '' : 's'}. Available: ${renderBalanceAvailable}.`}
                </span>
                {renderQuote.shortfall > 0 && (
                  <span className="text-red-400">
                    Need {renderQuote.shortfall} more credit{renderQuote.shortfall === 1 ? '' : 's'}.
                  </span>
                )}
                {!renderQuote.loading && !renderQuote.shortfall && renderQuote.error && (
                  <span className="text-red-400">{renderQuote.error}</span>
                )}
              </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-zinc-400">Optional: preview a concept image of the build.</div>
                  <button className={primary} onClick={visualizeBuild} disabled={disableRender}>
                    {rendering
                      ? 'Rendering...'
                      : `Visualize Build (${renderCreditsNeeded} credit${renderCreditsNeeded === 1 ? '' : 's'})`}
                   </button>
                 </div>
             </div>

            <div className={`${subPanel} p-4`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs sm:text-sm uppercase tracking-wide text-zinc-400">
                    Build Guide (PDF)
                  </div>
                  <p className="hidden sm:block text-xs text-zinc-500">
                    Step by step instructions with part numbers and diagrams.
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {guideTitle && (
                    <div className="text-xs text-amber-300 text-right max-w-xs">{guideTitle}</div>
                  )}
                  <div className="flex gap-2 flex-wrap justify-end">
                    <button
                      className={action}
                      onClick={() => setShowGuidePreview((p) => !p)}
                      disabled={!guideUrl}
                    >
                      {showGuidePreview ? 'Hide Preview' : 'Preview'}
                    </button>
                    <button
                      className={primary}
                      onClick={generateGuide}
                      disabled={generatingGuide || !materials.length}
                    >
                      {generatingGuide ? 'Generating' : 'Create Guide'}
                    </button>
                  </div>
                </div>
              </div>

              {guideUrl && showGuidePreview && (
                <div className="mt-3 rounded-xl border border-white/10 overflow-hidden">
                  <object data={guideUrl} type="application/pdf" className="w-full" style={{ height: '70vh' }}>
                    <div className="p-3 text-sm text-zinc-400">
                      PDF preview not supported. {sanitizeExternalUrl(guideUrl) ? (
                        <a className="underline" href={sanitizeExternalUrl(guideUrl)} target="_blank" rel="noreferrer">Open</a>
                      ) : (
                        <span>Open</span>
                      )}
                    </div>
                  </object>
                  <div className="flex justify-end gap-2 border-t border-white/10 bg-white/5 p-2">
                    {sanitizeExternalUrl(guideUrl) ? (
                      <>
                        <a href={sanitizeExternalUrl(guideUrl)} download={guideDownloadName} className={action}>Download</a>
                        <a href={sanitizeExternalUrl(guideUrl)} target="_blank" rel="noreferrer" className={action}>
                          Open
                        </a>
                      </>
                    ) : (
                      <span className="text-xs text-zinc-400">Preview unavailable</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
          )}

          {/* Bottom CTA */}
        </div>

        {/* Community Builds Carousel */}
        {communityBuilds.length > 0 && (
          <div className="mt-8 px-4 sm:px-6 lg:px-10 pb-10">
            <div className="mx-auto max-w-7xl">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold text-white">
                    Need more ideas?
                  </h2>
                  <p className="mt-1 text-sm text-zinc-400">Check out the Community Builds</p>
                </div>
                <Link
                  to="/community-builds"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 text-sm text-zinc-100 hover:border-amber-300/60 hover:text-white transition"
                >
                  View All
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>

              <div className="relative">
                {/* Carousel */}
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-out gap-4"
                    style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
                  >
                    {communityBuilds.map((build) => (
                      <Link
                        key={build.id}
                        to={`/community-builds/${build.id}`}
                        className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/3 xl:w-1/4"
                      >
                        <div className="group rounded-2xl border border-white/10 bg-white/5 overflow-hidden hover:border-amber-300/40 transition-all hover:shadow-[0_20px_60px_rgba(246,196,82,0.2)]">
                          {build.render_url ? (
                            <div className="aspect-video overflow-hidden bg-zinc-900/50">
                              <img
                                src={build.render_url}
                                alt={build.title || 'Community build'}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ) : (
                            <div className="aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                              <svg className="w-12 h-12 text-zinc-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                <path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          )}
                          <div className="p-4">
                            <h3 className="font-semibold text-zinc-100 group-hover:text-amber-300 transition-colors line-clamp-2">
                              {build.title || 'Untitled Build'}
                            </h3>
                            {build.description && (
                              <p className="mt-2 text-sm text-zinc-400 line-clamp-2">
                                {build.description}
                              </p>
                            )}
                            <div className="mt-3 flex items-center gap-3 text-xs text-zinc-500">
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                  <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {build.vote_count || 0}
                              </span>
                              {build.author_username && (
                                <span>by {build.author_username}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Navigation arrows */}
                {communityBuilds.length > 4 && (
                  <>
                    <button
                      onClick={() => setCarouselIndex((prev) => Math.max(0, prev - 1))}
                      disabled={carouselIndex === 0}
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 rounded-full border border-white/20 bg-zinc-900/90 p-2 text-white hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition backdrop-blur-sm"
                      aria-label="Previous"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setCarouselIndex((prev) => Math.min(communityBuilds.length - 1, prev + 1))}
                      disabled={carouselIndex >= communityBuilds.length - 1}
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 rounded-full border border-white/20 bg-zinc-900/90 p-2 text-white hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition backdrop-blur-sm"
                      aria-label="Next"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Dots indicator */}
              {communityBuilds.length > 4 && (
                <div className="mt-4 flex justify-center gap-2">
                  {communityBuilds.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCarouselIndex(idx)}
                      className={`h-2 rounded-full transition-all ${
                        idx === carouselIndex
                          ? 'w-8 bg-amber-400'
                          : 'w-2 bg-white/30 hover:bg-white/50'
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    {shareOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div
          className="absolute inset-0 bg-black/60"
          onClick={() => !sharePosting && setShareOpen(false)}
        />
        <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-zinc-900/90 p-5 shadow-2xl">
          <div className="mb-3">
            <h3 className="text-lg font-semibold text-amber-200">Share to Community Builds</h3>
            <p className="text-sm text-zinc-400">
              Add a title and optional description. Materials and the current render (if any) will be shared.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-zinc-300 mb-1">Title</label>
              <input
                className="w-full rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                value={shareTitle}
                onChange={(e) => setShareTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-1">Description (optional)</label>
              <textarea
                className="w-full rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                rows={3}
                value={shareDescription}
                onChange={(e) => setShareDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-white/15 px-3 py-2 text-sm text-zinc-200 hover:bg-white/5"
              onClick={() => setShareOpen(false)}
              disabled={sharePosting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-gradient-to-r from-amber-300 to-amber-500 px-4 py-2 text-sm font-semibold text-zinc-900 shadow disabled:opacity-60"
              onClick={shareToCommunity}
              disabled={sharePosting}
            >
              {sharePosting ? 'Sharing...' : 'Share'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
